/**
 * Bootstrap type definitions to avoid TypeScript errors
 */
declare namespace bootstrap {
    class Modal {
        constructor(element: HTMLElement, options?: any);
        show(): void;
        hide(): void;
        dispose(): void;
    }
}

/**
 * Configuration interface for device removal modal
 */
export interface DeviceModalConfig {
    /** Container element selector where the modal should be injected */
    containerSelector?: string;
    /** CSS classes for the modal backdrop (space-separated) */
    backdropClass?: string;
    /** CSS classes for the modal dialog (space-separated) */
    dialogClass?: string;
    /** CSS classes for the modal content (space-separated) */
    contentClass?: string;
}

/**
 * Device removal modal functionality
 * Injects and manages a Bootstrap modal for device removal confirmation
 */
export class DeviceModal {
    // Default configuration values
    private static readonly DEFAULT_CONTAINER_SELECTOR = 'body';
    private static readonly DEFAULT_BACKDROP_CLASS = 'modal fade';
    private static readonly DEFAULT_DIALOG_CLASS = 'modal-dialog modal-sm modal-dialog-centered modal-dialog-scrollable';
    private static readonly DEFAULT_CONTENT_CLASS = 'modal-content';

    // Configuration with applied defaults
    private config: Required<DeviceModalConfig>;

    // Track the modal instance
    private modalElement: HTMLElement | null = null;
    private modalInstance: any = null;

    /**
     * Initialize device removal modal functionality across the document
     * @param config Optional configuration to override defaults
     */
    public static initialize(config?: DeviceModalConfig): void {
        document.addEventListener('DOMContentLoaded', () => {
            // Create a singleton instance that will handle all device removals
            const deviceModal = new DeviceModal(config);

            // Find all device removal buttons
            const removalButtons = document.querySelectorAll('button[data-ebc-device-remove]');

            removalButtons.forEach(button => {
                // Set up click event handler for each button
                button.addEventListener('click', (event) => {
                    const target = event.currentTarget as HTMLElement;
                    const deviceName = target.dataset.ebcDeviceName;
                    const tokenId = target.dataset.ebcTokenId;
                    const returnUrl = target.dataset.ebcReturnUrl;
                    const formAction = target.dataset.ebcFormAction;

                    if (formAction && deviceName && tokenId) {
                        deviceModal.showModal(deviceName, tokenId, returnUrl || '', formAction);
                        event.preventDefault();
                    }
                });
            });
        });
    }

    /**
     * Create a new device modal handler
     * @param config Optional configuration to override defaults
     */
    constructor(config?: DeviceModalConfig) {
        // Apply defaults to each config property individually
        this.config = {
            containerSelector: config?.containerSelector ?? DeviceModal.DEFAULT_CONTAINER_SELECTOR,
            backdropClass: config?.backdropClass ?? DeviceModal.DEFAULT_BACKDROP_CLASS,
            dialogClass: config?.dialogClass ?? DeviceModal.DEFAULT_DIALOG_CLASS,
            contentClass: config?.contentClass ?? DeviceModal.DEFAULT_CONTENT_CLASS
        };

        // Create and inject the modal into the DOM
        this.injectModalMarkup();
    }

    /**
     * Create and inject the modal markup into the document
     */
    private injectModalMarkup(): void {
        const container = document.querySelector(this.config.containerSelector);
        if (!container) return;

        // Create modal element
        this.modalElement = document.createElement('div');
        this.modalElement.id = 'device-removal-modal';
        this.modalElement.className = this.config.backdropClass;
        this.modalElement.setAttribute('tabindex', '-1');
        this.modalElement.setAttribute('aria-labelledby', 'device-removal-modal-label');
        this.modalElement.setAttribute('aria-hidden', 'true');

        // Set the modal HTML content
        this.modalElement.innerHTML = `
            <div class="${this.config.dialogClass}">
                <div class="${this.config.contentClass}">
                    <div class="modal-header">
                        <h3 class="modal-title" id="device-removal-modal-label">Are you sure you want to sign this device out of your account?</h3>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p>Once signed out, ebooks downloaded to <strong id="device-name-placeholder"></strong> will no longer be available and the device will need to sign in again to read your ebooks.</p>
                    </div>
                    <form method="post" id="device-removal-form">
                        <div class="modal-footer">
                            <div class="d-grid gap-2 col-12">
                                <input type="hidden" name="__RequestVerificationToken" id="antiforgery-token-input">
                                <input type="hidden" name="TokenId" id="token-id-input">
                                <input type="hidden" name="ReturnUrl" id="return-url-input">
                                <input type="submit" class="btn btn-danger" value="Sign Out Device">
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        `;

        // Append the modal to the container
        container.appendChild(this.modalElement);

        // Set up form submit handler
        const form = this.modalElement.querySelector('#device-removal-form') as HTMLFormElement;
        if (form) {
            form.addEventListener('submit', this.handleFormSubmit.bind(this));
        }
    }

    /**
     * Show the modal with the specified device information
     * @param deviceName Name of the device to be removed
     * @param tokenId ID of the device token
     * @param returnUrl Return URL after removal
     * @param formAction Action URL for the form submission
     */
    public showModal(deviceName: string, tokenId: string, returnUrl: string, formAction: string): void {
        if (!this.modalElement) return;

        // Update device name in the modal text
        const deviceNameEl = this.modalElement.querySelector('#device-name-placeholder');
        if (deviceNameEl) {
            deviceNameEl.textContent = deviceName;
        }

        // Update the submit button text
        const submitButton = this.modalElement.querySelector('input[type="submit"]') as HTMLInputElement;
        if (submitButton) {
            submitButton.value = `Sign out ${deviceName}`;
        }

        // Set the hidden input values
        const tokenInput = this.modalElement.querySelector('#token-id-input') as HTMLInputElement;
        if (tokenInput) {
            tokenInput.value = tokenId;
        }

        const returnUrlInput = this.modalElement.querySelector('#return-url-input') as HTMLInputElement;
        if (returnUrlInput) {
            returnUrlInput.value = returnUrl;
        }

        // Copy the page-rendered antiforgery token into the modal form. The token element
        // is emitted by @Html.AntiForgeryToken() in the parent partial; without copying it
        // here, the server-side [ValidateAntiForgeryToken] check would reject the POST.
        const antiforgeryTokenInput = this.modalElement.querySelector('#antiforgery-token-input') as HTMLInputElement;
        if (antiforgeryTokenInput) {
            const pageToken = document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]:not(#antiforgery-token-input)');
            antiforgeryTokenInput.value = pageToken?.value ?? '';
        }

        // Update form action
        const form = this.modalElement.querySelector('#device-removal-form') as HTMLFormElement;
        if (form) {
            form.action = formAction;
        }

        // Show the modal using Bootstrap's modal API
        if (typeof bootstrap !== 'undefined') {
            if (!this.modalInstance) {
                this.modalInstance = new bootstrap.Modal(this.modalElement);
            }
            this.modalInstance.show();
        }
    }

    /**
     * Handle form submission for device removal
     * @param event Form submit event
     */
    private handleFormSubmit(event: Event): void {
        // Additional validation or processing can be added here if needed

        // Hide the modal after submission (optional)
        if (this.modalInstance) {
            this.modalInstance.hide();
        }
    }
}

// Initialize the device modal functionality
DeviceModal.initialize();